import React, { useEffect, useMemo } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { hasPermission } from "../../utils/permission";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { MdCheck, MdRemove } from "react-icons/md";

/**
 * 角色權限表（設計稿 #screen-roles，el-pm-template_4.html:2435-2496）
 *
 * 純閱讀頁：一個角色一張卡，一條權限一行，✓ 是做得到、― 是做不到。
 * 刻意**不做成表格** —— 舊的 11×4 勾選矩陣要左右對照才看得懂，而看這一頁的人
 * 通常只想知道「我（或他）到底能不能做這件事」，那是一行字就講得完的事。
 *
 * 可編輯的 11×4 權限矩陣仍然在 /Setting/Roles/Matrix（pages/Setting/Role.js），
 * 一行都沒有動。這一頁只是把它前面補上一份白話說明，並且把危險的編輯動作
 * 收到頁尾一顆 text 按鈕後面（硬規則 8：功能一個都沒少）。
 *
 * ── 五張卡 vs. 六種角色 ────────────────────────────────────────────
 * 設計稿是 5 張卡但涵蓋 6 種角色：開發與測試的權限一模一樣，合併成一張
 * （見設計稿 L2481）。畫面索引頁 L2596 自己寫的是「六種角色」，兩邊沒有矛盾，
 * 只是一個在數卡片、一個在數角色。這裡照設計稿的卡片分法做，
 * 但文字上不要說成「五種角色」。
 *
 * ── 這一頁的角色名稱是「職務分工」，不是資料庫裡的角色列 ─────────────
 * 下面五張卡是公司對每個職務該做什麼的白話說明（設計稿的內容）。
 * 系統裡實際建立了哪些角色是另一回事 —— role 資料表的內容由管理員自己維護，
 * 初始資料只有一筆「超級管理員」。兩者對不上的時候不能裝作一樣，
 * 所以頁尾另外列出 state.Role.List 的實際角色（硬規則 10）。
 *
 * 也**不要**跟 design/domain.js 的 MEMBER_ROLE_BY_CODE（專案經理／開發／測試／
 * 設計／觀察者）混用：那是「在某個專案裡擔任什麼」，是第三份完全不同的列舉。
 *
 * 整頁沒有 contained 主要按鈕 —— 這是一頁說明，沒有主要動作（硬規則 1）。
 */

/* 做得到／做不到的標記。圖示本身帶 aria-label，報讀軟體才念得出這一條是能做還是不能做 */
const MARK_STYLE = { verticalAlign: "-0.125em" };
const CanMark = () => <MdCheck role="img" aria-label="可執行" style={MARK_STYLE} />;
const CannotMark = () => <MdRemove role="img" aria-label="不可執行" style={MARK_STYLE} />;

/** 五張卡的內容逐字取自設計稿 L2440-2494（人名範例的處理見下方註解）；每條的第一格 true＝做得到、false＝做不到 */
const ROLE_CARDS = [
  {
    name: "高階主管",
    lines: [
      [true, "檢視所有專案的進度與時程"],
      [true, "於任何專案設定、修改里程碑"],
      [false, "不負責排定個別工項或回報進度"],
    ],
  },
  {
    name: "專案經理",
    lines: [
      [true, "建立專案、新增成員、新增工項"],
      [true, "設定、修改自己建立的里程碑（他人建立的須由建立者修改）"],
      [true, "審核送審的工項"],
      /* 設計稿這一條的括號裡是「會記成『黃子銘代林怡君回報』」——
         那兩個是範本假人名，換成不指名的寫法，意思不變（硬規則 10） */
      [true, "代他人回報進度（紀錄將註明代理人與被代理人）"],
    ],
  },
  {
    name: "RD 主管",
    lines: [
      [true, "將工項排入里程碑、指派負責人、排定先後順序"],
      [true, "檢視團隊成員各自負責的工項數"],
      [false, "無法修改里程碑的日期，僅能請設定者調整"],
    ],
  },
  {
    name: "開發、測試",
    lines: [
      [true, "檢視自己的工項、回報進度、送交審核人"],
      [true, "檢視自己的工項排定於哪個里程碑之前"],
      [false, "無法修改他人的工項，亦無法修改里程碑"],
    ],
  },
  {
    name: "系統管理員",
    lines: [
      [true, "建立帳號、指派角色、停用帳號、重設密碼"],
      [true, "設定專案編碼規則與通知寄送"],
      [false, "不檢視專案內容，除非同時為專案成員"],
    ],
  },
];

function SettingRoles(props) {
  const navigate = useNavigate();
  const { roleList, canEditMatrix, GET_RoleList } = props;

  useEffect(() => {
    GET_RoleList(null, null);
  }, [GET_RoleList]);

  const roles = useMemo(() => (Array.isArray(roleList) ? roleList : []), [roleList]);

  return (
    <>
      <Typography variant="h1" component="h1">
        角色權限說明
      </Typography>
      {/*
        設計稿 L2438 原文是「看不懂就照這張表對：左邊是動作，右邊打勾的角色才做得到」，
        那句在描述舊的勾選表格。這一頁已經改成一個角色一張卡，照抄那句話會叫
        使用者去找一張不存在的表，所以改寫成描述現在這個版面的句子。
      */}
      <Typography sx={{ mt: 1.5 }}>
        每位使用者僅有一個角色。以下每張卡說明一個角色，<CanMark /> 表示可執行，<CannotMark /> 表示不可執行。
        開發與測試的權限相同，因此合併為同一張。
      </Typography>

      <Stack spacing={2} sx={{ mt: 3 }}>
        {ROLE_CARDS.map((r) => (
          <Card component="section" key={r.name}>
            <CardContent>
              <Typography variant="h3" component="h2">
                {r.name}
              </Typography>
              {/*
                設計稿每張卡的第二行是「例如：林總」「例如：黃子銘」這種人名範例，
                全部是範本假資料，這裡整行不畫（硬規則 10）。
                要真的舉例得先知道系統裡誰是哪個角色，而上面這五個職務名稱
                跟資料庫裡的角色列本來就不保證對得起來。
              */}
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                {r.lines.map(([can, text]) => (
                  <Typography key={text}>
                    {can ? <CanMark /> : <CannotMark />} {text}
                  </Typography>
                ))}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* ---------- 系統裡實際有哪些角色 ---------- */}
      {/*
        上面五張卡講的是「職務上應該能做什麼」，這一張講的是「這套系統裡
        目前真的建了哪幾個角色」。兩者常常對不起來（角色是管理員自己新增的），
        不併在一起講，免得有人以為上面五個名字就是下拉選單裡會出現的選項。
        一筆都沒有時整塊不畫（硬規則 2）。
      */}
      {roles.length > 0 ? (
        <Card component="section" sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h3" component="h2">
              系統目前已建立的角色
            </Typography>
            <Typography sx={{ mt: 1 }}>
              新增人員時的「角色」選單即來自此處。名稱不一定與上方的職務說明相同 ——
              角色由管理員自行建立，如需對應，請依上方說明調整為一致的名稱。
            </Typography>
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {roles.map((r) => {
                const id = _.get(r, "id");
                const nm = _.get(r, "name", "");
                const note = String(_.get(r, "note", "") || "").trim();
                const count = _.get(r, "permission_count", null);
                return (
                  <Typography key={id}>
                    <strong>{nm}</strong>
                    {note ? `　${note}` : ""}
                    {/* 權限筆數是後端真的回得出來的（RoleList 的 permission_count） */}
                    {count != null ? `　（${count} 項權限）` : ""}
                  </Typography>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {/* ---------- 編輯權限矩陣 ---------- */}
      {/*
        設計稿說這一頁沒有動作按鈕，唯一的例外就是這一顆：逐格勾選的權限矩陣
        還在 /Setting/Roles/Matrix，沒有入口就等於被刪掉了（硬規則 8）。
        用 text 而不是 outlined，是因為絕大多數看這一頁的人
        只是想確認自己能做什麼，不該被一顆顯眼的按鈕引誘去改權限。
      */}
      {canEditMatrix ? (
        <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 4 }}>
          <Button variant="text" type="button" onClick={() => navigate("/Setting/Roles/Matrix")}>
            編輯權限矩陣
          </Button>
          <Typography variant="body2" color="text.secondary" component="span">
            變更後將立即影響該角色的所有使用者。
          </Typography>
        </Stack>
      ) : null}
    </>
  );
}

const mapStateToProps = (state) => ({
  roleList: _.get(state, "Role.List", []),
  /* 只有拿得到 role 權限的人才看得到編輯矩陣的入口；真正的把關在後端 */
  canEditMatrix: hasPermission("role"),
});

const mapDispatchToProps = (dispatch) => ({
  GET_RoleList: (callback, loading) => dispatch({ type: "GET_RoleList", callback, loading }),
});

SettingRoles.propTypes = {
  roleList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  canEditMatrix: PropTypes.bool,
  GET_RoleList: PropTypes.func,
};

SettingRoles.defaultProps = { roleList: [], canEditMatrix: false, GET_RoleList: () => {} };

export default connect(mapStateToProps, mapDispatchToProps)(SettingRoles);
